import React, { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { Row, Col, Card, CardHeader, CardBody, Spinner } from "reactstrap"
import { fetchCurrentSubscription } from "../../store/subscription/actions"
import { getMyCollectionUsage, getPlanFeatureCatalog, getPlanIntegrationCatalog, getMyConnectedIntegrations } from "../../api/requests/subscription-requests"
import { getMyAddons } from "../../api/requests/addon-requests"
import "../../assets/css/page-common.css"
import BackButton from "../../components/Common/BackButton";
import { usePlanFeatures } from "../../hooks/usePlanFeatures";
import { formatDaysLeft, formatLicenseDate, isUnlimitedDate } from "../../utils/subscriptionDates"

const NAVY = "#221C5D"
const GREEN = "#71C30B"
const ORANGE = "#E56A22"
const RED = "#dc3545"
// Ek modülle (360 Market) gelen haklar bu renkle ayrışır.
const MODULE = "#0284C7"

const ModuleBadge = ({ children, title }) => (
  <span
    title={title || "Ek modülle eklendi"}
    style={{
      display: "inline-flex", alignItems: "center", gap: ".2rem", marginLeft: ".4rem",
      borderRadius: 999, padding: ".1rem .45rem", fontSize: ".68rem", fontWeight: 700,
      background: `${MODULE}18`, color: MODULE, verticalAlign: "middle",
    }}
  >
    <i className="bx bx-plus" style={{ fontSize: 11 }} />{children}
  </span>
)

const LimitBar = ({ used, max, color }) => (
  <div className="progress-bar-track">
    <div
      className="progress-bar-fill"
      style={{ width: `${Math.min((used || 0) / max, 1) * 100}%`, backgroundColor: color }}
    />
  </div>
)

const statusMap = {
  ACTIVE: { label: "Aktif", color: GREEN, icon: "bx-check-circle" },
  TRIAL: { label: "Deneme", color: "#F2C96D", icon: "bx-time-five" },
  EXPIRED: { label: "Süresi Dolmuş", color: RED, icon: "bx-error" },
  PENDING: { label: "Ödeme Bekliyor", color: ORANGE, icon: "bx-loader-alt" },
  CANCELLED: { label: "İptal Edildi", color: RED, icon: "bx-x-circle" },
  SCHEDULED: { label: "Planlandı", color: NAVY, icon: "bx-calendar-check" },
}

const parseFeatures = (f) => {
  if (!f) return []
  if (Array.isArray(f)) return f
  if (typeof f === "string") {
    const trimmed = f.trim()
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed)
        if (Array.isArray(parsed)) return parsed
        if (parsed.items && Array.isArray(parsed.items)) return parsed.items
      } catch {}
    }
    return f.split("\n").filter((s) => s.trim())
  }
  return []
}

const formatPrice = (price) => {
  if (price == null) return "-"
  return new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(price) + " ₺"
}

const formatDate = (d) => formatLicenseDate(d)

const SubscriptionDetails = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { currentStatus, loading } = useSelector((state) => state.subscription)

  document.title = "Aboneliğim | Tahsilat360"

  // Tahsilat limiti kullanımı ayrı uçtan gelir (dönem içi sayaç); abonelik
  // durumundan bağımsız olduğu için redux'a değil yerel state'e alınır.
  const [usage, setUsage] = useState(null)
  // Entegrasyon kataloğu uçtan gelir; yeni bir sağlayıcı eklendiğinde bu ekran da
  // kendiliğinden güncellenir, sabit liste tutulmaz.
  const [integrationCatalog, setIntegrationCatalog] = useState([])
  // Paket kapsamı ile bağlantı durumu ayrı: paket İşyerimPOS'a izin verse de firma
  // henüz bağlantıyı kurmamış olabilir. Rozet ikisini ayırt etsin diye ayrı çekilir.
  const [connectedIntegrationCodes, setConnectedIntegrationCodes] = useState([])
  const [featureCatalog, setFeatureCatalog] = useState([])
  const [addons, setAddons] = useState([])
  // Özellik denemeleri panel genelinde tek kaynaktan gelir (PlanFeatureProvider);
  // burada ayrı istek atılmaz. Süresi dolanlar da listede kalır — kullanıcı hakkını
  // kullandığını görebilsin.
  const { trials } = usePlanFeatures()

  useEffect(() => {
    dispatch(fetchCurrentSubscription())
  }, [dispatch])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await getMyCollectionUsage()
        if (!cancelled) setUsage(res?.data || null)
      } catch {
        // Limit bilgisi alınamazsa bölüm gizlenir.
      }
    })()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await getPlanIntegrationCatalog()
        if (!cancelled) setIntegrationCatalog(res?.data || [])
      } catch {
        // Katalog alınamazsa entegrasyon bölümü hiç çizilmez; sayfanın geri
        // kalanı çalışmaya devam eder.
      }
    })()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await getMyConnectedIntegrations()
        if (!cancelled) setConnectedIntegrationCodes(res?.data || [])
      } catch {
        // Bağlantı durumu alınamazsa rozetler "bağlanabilir" olarak çizilir;
        // yanlışlıkla "aktif" demektense eksik bilgi vermek yeğdir.
      }
    })()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await getMyAddons()
        if (!cancelled) setAddons((res?.data || []).filter((a) => a.status === "ACTIVE" && a.effective))
      } catch {
        // Modüller alınamazsa bölüm çizilmez.
      }
    })()
    return () => { cancelled = true }
  }, [])

  // Özellik kataloğu: kod → etiket/kategori. Paketin özellik kümesi kod olarak
  // geldiği için etiketler buradan çözülür.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await getPlanFeatureCatalog()
        if (!cancelled) setFeatureCatalog(res?.data || [])
      } catch {
        // Katalog alınamazsa özellik bölümü çizilmez.
      }
    })()
    return () => { cancelled = true }
  }, [])

  const addonFeatureCodes = useMemo(
    () => [...new Set(addons.flatMap((a) => a.grantedFeatures || []))],
    [addons]
  )
  const addonIntegrationCodes = useMemo(
    () => [...new Set(addons.flatMap((a) => a.grantedIntegrations || []))],
    [addons]
  )

  // Pakette olmayıp modülle açılan özellikler fromAddon ile işaretlenir.
  const featureGroups = useMemo(() => {
    const planCodes = currentStatus?.planFeatures || []
    const codes = [...planCodes, ...addonFeatureCodes]
    if (codes.length === 0) return []
    const groups = []
    featureCatalog
      .filter((f) => codes.includes(f.code))
      .forEach((f) => {
        let g = groups.find((x) => x.category === f.category)
        if (!g) { g = { category: f.category, items: [] }; groups.push(g) }
        g.items.push({ ...f, fromAddon: !planCodes.includes(f.code) })
      })
    return groups
  }, [featureCatalog, currentStatus, addonFeatureCodes])

  // Paketin izin verdiği entegrasyonlar. Küme BOŞSA kısıtlama yok demektir
  // (V96 semantiği); o durumda katalogun tamamı geçerlidir. `sub` aşağıda
  // tanımlandığı için burada doğrudan currentStatus okunur; ikisi aynı nesnedir.
  const planIntegrationCodes = currentStatus?.planIntegrations || []
  const enabledIntegrationCodes = planIntegrationCodes.length > 0
    ? [...new Set([...planIntegrationCodes, ...addonIntegrationCodes])]
    : integrationCatalog.map((i) => i.code)
  const integrationFromAddon = (code) =>
    planIntegrationCodes.length > 0 && !planIntegrationCodes.includes(code)

  // Yalnızca pakete DAHİL olanlar listelenir. Önceden katalogun tamamı çizilip
  // pakette olmayanlar gri + kilitli gösteriliyordu; yönetici bir entegrasyonu
  // paketten çıkardığında müşteri onu ekranda görmeye devam ettiği için
  // "çıkardım ama duruyor" izlenimi doğuruyordu.
  /** Katalog kategoriye göre gruplanır; sıra backend'den geldiği gibi korunur. */
  const integrationGroups = useMemo(() => {
    const groups = []
    integrationCatalog
      .filter((i) => enabledIntegrationCodes.includes(i.code))
      .forEach((i) => {
        let g = groups.find((x) => x.category === i.category)
        if (!g) { g = { category: i.category, items: [] }; groups.push(g) }
        g.items.push(i)
      })
    return groups
  }, [integrationCatalog, enabledIntegrationCodes])

  /** Pakete dahil OLAN ve bağlantısı kurulmuş entegrasyon sayısı. */
  const connectedCount = useMemo(
    () => enabledIntegrationCodes.filter((code) => connectedIntegrationCodes.includes(code)).length,
    [enabledIntegrationCodes, connectedIntegrationCodes]
  )

  // Not: bu hook erken return'lerin ÜSTÜNDE durmak zorunda. Altta olduğunda ilk
  // render'da (yükleniyor) hiç çağrılmıyor, veri gelince çağrılıyordu; React bunu
  // "Rendered more hooks than during the previous render" ile reddediyor.
  if (loading && !currentStatus) {
    return (
      <div className="page-content text-center p-5">
        <Spinner size="lg" color="primary" />
        <h6 className="mt-3 text-muted">Yükleniyor...</h6>
      </div>
    )
  }

  if (!currentStatus) {
    return (
      <div className="page-content page-common">
        <div className="container-fluid text-center py-5">
          <i className="bx bx-package display-3 d-block mb-3 text-muted" />
          <h5 className="text-muted mb-3">Aktif aboneliğiniz bulunmamaktadır</h5>
          <button
            className="btn btn-navy btn-rounded px-4"
            onClick={() => navigate("/subscription/plans")}
          >
            <i className="bx bx-cart me-1" />Paketleri İncele
          </button>
        </div>
      </div>
    )
  }

  const sub = currentStatus
  const st = statusMap[sub.status] || statusMap.EXPIRED
  const features = parseFeatures(sub.features)

  const accountRatio = sub.maxAccounts ? Math.min((sub.currentAccounts || 0) / sub.maxAccounts, 1) : null
  const userRatio = sub.maxUsers ? Math.min((sub.currentUsers || 0) / sub.maxUsers, 1) : null
  const addonSum = (field) => addons.reduce((n, a) => n + (Number(a[field]) || 0), 0)
  const addonAccounts = addonSum("grantedMaxAccounts")
  const addonUsers = addonSum("grantedMaxUsers")
  const addonAmount = addonSum("grantedCollectionAmount")
  const addonCount = addonSum("grantedCollectionCount")
  const addonSummary = (a) => [
    a.grantedMaxAccounts ? `+${a.grantedMaxAccounts} cari hesap` : null,
    a.grantedMaxUsers ? `+${a.grantedMaxUsers} kullanıcı` : null,
    a.grantedCollectionAmount ? `+${formatPrice(a.grantedCollectionAmount)} tahsilat` : null,
    a.grantedCollectionCount ? `+${a.grantedCollectionCount} tahsilat adedi` : null,
    a.grantedFeatures?.length ? `${a.grantedFeatures.length} özellik` : null,
    a.grantedIntegrations?.length ? `${a.grantedIntegrations.length} entegrasyon` : null,
  ].filter(Boolean).join(" · ")
  const getBarColor = (ratio) => {
    if (ratio >= 1) return RED
    if (ratio >= 0.8) return ORANGE
    return GREEN
  }

  // Süre ilerleme
  const totalDays = sub.startDate && sub.endDate
    ? Math.max(1, (new Date(sub.endDate) - new Date(sub.startDate)) / 86400000)
    : 1
  const elapsed = sub.startDate ? Math.max(0, (new Date() - new Date(sub.startDate)) / 86400000) : 0
  // Süresiz pakette çubuk dolu kalır; aksi hâlde 9999'a göre oran hep ~0 olurdu.
  const unlimited = isUnlimitedDate(sub.endDate)
  const timeRatio = unlimited ? 0 : Math.min(elapsed / totalDays, 1)
  /**
   * Paketin izin verdiği entegrasyonlar. Boş küme "kısıtlama yok" demektir;
   * o durumda katalogun tamamı açık gösterilir.
   */
  // Tahsilat limiti kullanımı — paket limitliyse dolum çubuklarını besler.
  const amountRatio = usage?.maxAmount
    ? Math.min((Number(usage.usedAmount) || 0) / Number(usage.maxAmount), 1)
    : 0
  const countRatio = usage?.maxCount
    ? Math.min((Number(usage.usedCount) || 0) / Number(usage.maxCount), 1)
    : 0

  const timeColor = unlimited ? NAVY : sub.daysRemaining <= 7 ? RED : sub.daysRemaining <= 30 ? ORANGE : NAVY

  // Abonelik bilgi satırları. Paket süresi (14 gün / 12 ay) V93 ile geldi; paketin
  // ne kadarlık bir dönem olduğunu görmek için burada da gösterilir.
  const infoRows = [
    { icon: "bx-calendar-plus", color: GREEN, label: "Başlangıç", value: formatDate(sub.startDate) },
    { icon: "bx-calendar-check", color: RED, label: "Bitiş", value: formatDate(sub.endDate) },
    ...(sub.durationLabel
      ? [{ icon: "bx-calendar", color: NAVY, label: "Paket Süresi", value: sub.durationLabel }]
      : []),
    { icon: "bx-wallet", color: ORANGE, label: "Ödenen Tutar", value: formatPrice(sub.paidAmount) },
    { icon: "bx-time-five", color: timeColor, label: "Kalan Süre", value: formatDaysLeft(sub.daysRemaining ?? 0, sub.endDate) },
  ]

  return (
    <div className="page-content page-common">
      <div className="container-fluid">

        {/* ── Page Header ── */}
        <div data-tour="subscription-details" className="page-header d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="d-flex align-items-center gap-3">
            <BackButton />
            <div>
              <div className="d-flex align-items-center gap-2">
                <h5 className="mb-0 fw-bold" style={{ color: "#1e293b" }}>
                  {sub.planName || "Abonelik"}
                </h5>
                <span
                  className="badge d-inline-flex align-items-center gap-1"
                  style={{
                    borderRadius: 999,
                    padding: ".3rem .65rem",
                    backgroundColor: `${st.color}18`,
                    color: st.color,
                    fontWeight: 600,
                    fontSize: ".75rem",
                    border: `1px solid ${st.color}30`,
                  }}
                >
                  <i className={`bx ${st.icon}`} style={{ fontSize: 13 }} />
                  {st.label}
                </span>
              </div>
              <span className="text-muted" style={{ fontSize: ".82rem" }}>
                Abonelik Detayı
              </span>
            </div>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <button
              className="btn btn-navy btn-rounded d-flex align-items-center gap-1 px-3"
              style={{ height: 34, fontSize: ".8rem" }}
              onClick={() => navigate("/subscription/plans")}
            >
              <i className="bx bx-refresh" style={{ fontSize: 16 }} /> Yenile / Yükselt
            </button>
            <button
              className="btn btn-rounded d-flex align-items-center gap-1 px-3"
              style={{ height: 34, fontSize: ".8rem", backgroundColor: "#E56A22", borderColor: "#E56A22", color: "#fff" }}
              onClick={() => navigate("/subscription/referrals")}
            >
              <i className="bx bx-gift" style={{ fontSize: 16 }} /> Arkadaşını Getir
            </button>
            <button
              className="btn btn-light btn-rounded d-flex align-items-center gap-1 px-3"
              style={{ height: 34, fontSize: ".8rem" }}
              onClick={() => navigate("/subscription/history")}
            >
              <i className="bx bx-history" style={{ fontSize: 16 }} /> Geçmiş
            </button>
          </div>
        </div>

        {sub.pendingPlanName && (
          <div
            className="d-flex align-items-start gap-2 mb-3 p-3"
            style={{ backgroundColor: "#f0f4ff", borderRadius: ".75rem", border: `1px solid ${NAVY}20`, fontSize: ".85rem" }}
          >
            <i className="bx bx-transfer-alt" style={{ color: NAVY, fontSize: 18, marginTop: 1 }} />
            <span>
              Mevcut paketiniz <strong>{formatDate(sub.endDate)}</strong> tarihinde sona erdiğinde{" "}
              <strong>{sub.pendingPlanName}</strong> paketine geçilecek
              {sub.pendingPlanPaid ? " (bedeli ödendi)." : "."}
            </span>
          </div>
        )}

        {/* ── Kullanım Hakları & Abonelik Bilgileri ── */}
        <Row className="mb-3 g-3">
          <Col lg={6}>
            <Card className="detail-card h-100">
              <CardHeader>
                <div className="section-title">
                  <div className="section-icon" style={{ background: "rgba(34,28,93,.1)", color: NAVY }}>
                    <i className="bx bx-bar-chart-alt-2" />
                  </div>
                  Kullanım Hakları
                </div>
              </CardHeader>
              <CardBody>
                <div className="rights-grid">
                  {/* Süre */}
                  <div className="right-item">
                    <div className="right-header">
                      <span className="right-label">
                        <i className="bx bx-time-five me-1" />Kalan Süre
                      </span>
                      <span className="right-value" style={{ color: timeColor }}>
                        {formatDaysLeft(sub.daysRemaining ?? 0, sub.endDate)}
                      </span>
                    </div>
                    <div className="progress-bar-track">
                      <div
                        className="progress-bar-fill"
                        style={{ width: `${(1 - timeRatio) * 100}%`, backgroundColor: timeColor }}
                      />
                    </div>
                    <div className="d-flex justify-content-between mt-1" style={{ fontSize: ".72rem", color: "#94a3b8" }}>
                      <span>{formatDate(sub.startDate)}</span>
                      <span>{formatDate(sub.endDate)}</span>
                    </div>
                  </div>

                  {/* Cari Hesap */}
                  <div className="right-item">
                    <div className="right-header">
                      <span className="right-label">
                        <i className="bx bx-user me-1" />Cari Hesap
                      </span>
                      <span className="right-value">
                        {sub.currentAccounts ?? 0}
                        <span style={{ color: "#94a3b8", fontWeight: 400 }}> / {sub.maxAccounts ?? "Sınırsız"}</span>
                        {sub.maxAccounts != null && addonAccounts > 0 && (
                          <ModuleBadge>{addonAccounts} hesap</ModuleBadge>
                        )}
                      </span>
                    </div>
                    {accountRatio != null && (
                      <LimitBar used={sub.currentAccounts} max={sub.maxAccounts} color={getBarColor(accountRatio)} />
                    )}
                  </div>

                  {/* Kullanıcı */}
                  <div className="right-item">
                    <div className="right-header">
                      <span className="right-label">
                        <i className="bx bx-group me-1" />Kullanıcı
                      </span>
                      <span className="right-value">
                        {sub.currentUsers ?? 0}
                        <span style={{ color: "#94a3b8", fontWeight: 400 }}> / {sub.maxUsers ?? "Sınırsız"}</span>
                        {sub.maxUsers != null && addonUsers > 0 && (
                          <ModuleBadge>{addonUsers} kullanıcı</ModuleBadge>
                        )}
                      </span>
                    </div>
                    {userRatio != null && (
                      <LimitBar used={sub.currentUsers} max={sub.maxUsers} color={getBarColor(userRatio)} />
                    )}
                  </div>

                  {/* Tahsilat Limiti — paket limitliyse gösterilir (V93) */}
                  {usage?.limited && (
                    <>
                      {usage.maxAmount != null && (
                        <div className="right-item">
                          <div className="right-header">
                            <span className="right-label">
                              <i className="bx bx-wallet me-1" />Tahsilat Tutarı
                              <span style={{ color: "#94a3b8", fontWeight: 400, fontSize: ".72rem" }}>
                                {" "}({usage.period === "MONTHLY" ? "bu ay" : "bu dönem"})
                              </span>
                            </span>
                            <span className="right-value">
                              {formatPrice(usage.usedAmount || 0)}
                              <span style={{ color: "#94a3b8", fontWeight: 400 }}> / {formatPrice(usage.maxAmount)}</span>
                              {addonAmount > 0 && <ModuleBadge>{formatPrice(addonAmount)}</ModuleBadge>}
                            </span>
                          </div>
                          <LimitBar used={Number(usage.usedAmount)} max={Number(usage.maxAmount)} color={getBarColor(amountRatio)} />
                        </div>
                      )}

                      {usage.maxCount != null && (
                        <div className="right-item">
                          <div className="right-header">
                            <span className="right-label">
                              <i className="bx bx-receipt me-1" />Tahsilat Adedi
                              <span style={{ color: "#94a3b8", fontWeight: 400, fontSize: ".72rem" }}>
                                {" "}({usage.period === "MONTHLY" ? "bu ay" : "bu dönem"})
                              </span>
                            </span>
                            <span className="right-value">
                              {usage.usedCount ?? 0}
                              <span style={{ color: "#94a3b8", fontWeight: 400 }}> / {usage.maxCount}</span>
                              {addonCount > 0 && <ModuleBadge>{addonCount} adet</ModuleBadge>}
                            </span>
                          </div>
                          <LimitBar used={Number(usage.usedCount)} max={Number(usage.maxCount)} color={getBarColor(countRatio)} />
                        </div>
                      )}

                      {usage.exhausted && (
                        <div className="right-item">
                          <div style={{ fontSize: ".75rem", color: RED }}>
                            <i className="bx bx-error-circle me-1" />
                            Tahsilat limitiniz doldu; yeni tahsilat yapılamıyor.
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Özel Domain */}
                  <div className="right-item">
                    <div className="right-header">
                      <span className="right-label">
                        <i className="bx bx-globe me-1" />Özel Domain
                      </span>
                      <span
                        className="badge border-0"
                        style={{
                          borderRadius: 999,
                          padding: ".3rem .55rem",
                          backgroundColor: sub.allowCustomDomain ? `${GREEN}18` : "rgba(108,117,125,.1)",
                          color: sub.allowCustomDomain ? GREEN : "#6c757d",
                          fontWeight: 600,
                          fontSize: ".75rem",
                        }}
                      >
                        {sub.allowCustomDomain ? "Aktif" : "Yok"}
                      </span>
                    </div>
                  </div>

                  {/* Entegrasyonlar — paketin izin verdiği sanal POS ve muhasebe.
                      Boş küme "kısıtlama yok" demektir; o durumda katalogun tamamı
                      açık kabul edilir ve kullanıcıya öyle gösterilir. */}
                  {integrationGroups.length > 0 && (
                    <div className="right-item">
                      <div className="right-header mb-2">
                        <span className="right-label">
                          <i className="bx bx-plug me-1" />Entegrasyonlar
                        </span>
                        <span className="right-value" style={{ fontSize: ".78rem" }}>
                          {connectedCount} aktif / {enabledIntegrationCodes.length} kullanılabilir
                        </span>
                      </div>
                      {integrationGroups.map((g) => (
                        <div key={g.category} className="mb-2">
                          <div style={{ fontSize: ".68rem", textTransform: "uppercase", letterSpacing: ".04em", color: "#94a3b8", fontWeight: 700, marginBottom: ".25rem" }}>
                            {g.category}
                          </div>
                          <div className="d-flex flex-wrap gap-1">
                            {g.items.map((i) => {
                              // Yeşil tik yalnız BAĞLANTISI kurulmuş entegrasyonda. Paketin
                              // kapsaması onu aktif yapmaz; kullanıcı bağlantı kurmadan
                              // "aktif" yazısını görünce kurulmuş sanıyordu.
                              const connected = connectedIntegrationCodes.includes(i.code)
                              const viaAddon = integrationFromAddon(i.code)
                              return (
                                <span
                                  key={i.code}
                                  title={viaAddon
                                    ? "Ek modülle açıldı"
                                    : connected
                                      ? "Bağlantı kuruldu, kullanımda"
                                      : "Paketinizde var, henüz aktifleştirilmedi"}
                                  style={{
                                    display: "inline-flex", alignItems: "center", gap: ".25rem",
                                    borderRadius: 999, padding: ".2rem .55rem", fontSize: ".72rem", fontWeight: 600,
                                    background: viaAddon ? `${MODULE}18` : connected ? `${GREEN}18` : "#f1f5f9",
                                    color: viaAddon ? MODULE : connected ? GREEN : "#64748b",
                                    border: viaAddon || connected ? "none" : "1px dashed #cbd5e1",
                                  }}
                                >
                                  <i className={connected ? "bx bx-check" : "bx bx-plug"} />
                                  {i.label}
                                  <span style={{ fontWeight: 500, opacity: .85 }}>
                                    · {connected ? "Aktif" : "Bağlanabilir"}
                                  </span>
                                </span>
                              )
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Ücretsiz denemeler — paket dışı bir özellik için başlatılan süreler.
                      Aktif olanlar özelliği geçici olarak açar; süresi dolanlar hakkın
                      kullanıldığını gösterir (aynı özellik ikinci kez denenemez). */}
                  {trials.length > 0 && (
                    <div className="right-item">
                      <div className="right-header mb-2">
                        <span className="right-label">
                          <i className="bx bx-hourglass me-1" />Ücretsiz Denemeler
                        </span>
                        <span className="right-value" style={{ fontSize: ".78rem" }}>
                          {trials.filter((t) => t.active).length} aktif
                        </span>
                      </div>
                      <div className="d-flex flex-column gap-1">
                        {trials.map((t) => {
                          const sonGun = (t.remainingDays ?? 0) <= 1
                          const renk = !t.active ? "#6c757d" : sonGun ? RED : ORANGE
                          return (
                            <div
                              key={t.code}
                              className="d-flex align-items-center justify-content-between"
                              style={{ fontSize: ".78rem" }}
                            >
                              <span style={{ color: "#4b5563" }}>
                                <i
                                  className={`bx ${t.active ? "bx-time-five" : "bx-x-circle"} me-1`}
                                  style={{ color: renk }}
                                />
                                {t.label}
                              </span>
                              <span
                                className="badge border-0"
                                title={t.active
                                  ? `Deneme ${formatDate(t.endsAt)} tarihinde sona eriyor`
                                  : `Deneme ${formatDate(t.endsAt)} tarihinde sona erdi`}
                                style={{
                                  borderRadius: 999,
                                  padding: ".3rem .55rem",
                                  backgroundColor: `${renk}1a`,
                                  color: renk,
                                  fontWeight: 600,
                                  fontSize: ".72rem",
                                }}
                              >
                                {t.active ? `${t.remainingDays} gün kaldı` : "Süresi doldu"}
                              </span>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  <div className="right-item">
                    <div className="right-header mb-2">
                      <span className="right-label">
                        <i className="bx bx-shopping-bag me-1" />Satın Alınan Modüller
                      </span>
                      <span className="right-value" style={{ fontSize: ".78rem" }}>
                        {addons.length} aktif
                      </span>
                    </div>
                    {addons.length === 0 && (
                      <div style={{ fontSize: ".78rem", color: "#94a3b8" }}>-</div>
                    )}
                    <div className="d-flex flex-column gap-2">
                      {addons.map((a) => (
                        <div
                          key={a.id}
                          className="d-flex align-items-center justify-content-between gap-2"
                          style={{ fontSize: ".78rem" }}
                        >
                          <span style={{ color: "#4b5563", minWidth: 0 }}>
                            <i className="bx bx-package me-1" style={{ color: MODULE }} />
                            <span className="fw-semibold">{a.moduleName}</span>
                            {addonSummary(a) && (
                              <span className="d-block" style={{ color: "#94a3b8", fontSize: ".72rem", marginLeft: "1.15rem" }}>
                                {addonSummary(a)}
                              </span>
                            )}
                          </span>
                          <span
                            className="badge border-0 flex-shrink-0"
                            style={{
                              borderRadius: 999,
                              padding: ".3rem .55rem",
                              backgroundColor: `${MODULE}1a`,
                              color: MODULE,
                              fontWeight: 600,
                              fontSize: ".72rem",
                            }}
                          >
                            {a.endDate && !isUnlimitedDate(a.endDate) ? `${formatDate(a.endDate)} tarihine kadar` : "Süresiz"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col lg={6}>
            <Card className="detail-card h-100">
              <CardHeader>
                <div className="section-title">
                  <div className="section-icon" style={{ background: `${NAVY}18`, color: NAVY }}>
                    <i className="bx bx-id-card" />
                  </div>
                  Abonelik Bilgileri
                </div>
              </CardHeader>
              <CardBody className="p-0">
                {/* Paket Adı + Durum — vurgulu üst bant */}
                <div
                  className="d-flex align-items-center justify-content-between px-4 py-3"
                  style={{ background: `linear-gradient(135deg, ${NAVY}0C, ${NAVY}04)`, borderBottom: "1px solid #f0f2f5" }}
                >
                  <div>
                    <div style={{ fontSize: ".7rem", color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px" }}>Paket</div>
                    <div className="fw-bold" style={{ fontSize: "1.15rem", color: "#1e293b" }}>
                      <i className="bx bx-package me-1" style={{ color: NAVY }} />{sub.planName || "-"}
                    </div>
                  </div>
                  <span
                    className="d-inline-flex align-items-center gap-1"
                    style={{
                      borderRadius: 999,
                      padding: ".4rem .85rem",
                      backgroundColor: st.color,
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: ".78rem",
                      boxShadow: `0 2px 8px ${st.color}40`,
                    }}
                  >
                    <i className={`bx ${st.icon}`} style={{ fontSize: 14 }} />
                    {st.label}
                  </span>
                </div>

                {/* Bilgi satırları — ikonlu ve renkli */}
                <div className="px-4 py-3">
                  {infoRows.map((item, i) => (
                    <div
                      key={i}
                      className="d-flex align-items-center justify-content-between py-2"
                      style={{ borderBottom: i < infoRows.length - 1 ? "1px solid #f0f2f5" : "none" }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <div
                          style={{
                            width: 32, height: 32, borderRadius: 8,
                            backgroundColor: `${item.color}14`,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <i className={`bx ${item.icon}`} style={{ color: item.color, fontSize: 16 }} />
                        </div>
                        <span style={{ fontSize: ".85rem", color: "#64748b", fontWeight: 500 }}>{item.label}</span>
                      </div>
                      <span className="fw-bold" style={{ fontSize: ".92rem", color: "#1e293b" }}>{item.value}</span>
                    </div>
                  ))}
                </div>

                {(featureGroups.length > 0 || features.length > 0) && (
                  <div className="px-4 py-3" style={{ borderTop: "1px solid #f0f2f5" }}>
                    <div className="section-title mb-3">
                      <div className="section-icon" style={{ background: `${ORANGE}18`, color: ORANGE }}>
                        <i className="bx bx-star" />
                      </div>
                      Paket Özellikleri
                      {featureGroups.length > 0 && (
                        <span className="ms-2" style={{ fontSize: ".78rem", color: "#94a3b8", fontWeight: 500 }}>
                          {featureGroups.reduce((n, g) => n + g.items.length, 0)} özellik
                        </span>
                      )}
                      {addonFeatureCodes.length > 0 && (
                        <span className="ms-auto d-inline-flex align-items-center gap-1" style={{ fontSize: ".72rem", color: MODULE, fontWeight: 600 }}>
                          <span style={{ width: 8, height: 8, borderRadius: 999, background: MODULE }} />
                          Ek modülle açılan
                        </span>
                      )}
                    </div>
                    <div>
                      {featureGroups.map((g) => (
                        <div key={g.category} className="feature-line">
                          <span className="feature-line-title">{g.category}</span>
                          <div className="feature-line-items">
                            {g.items.map((f) => (
                              <span
                                key={f.code}
                                className="feature-item"
                                title={f.fromAddon ? "Ek modülle açıldı" : "Paketinizde açık"}
                                style={{ color: f.fromAddon ? MODULE : "#1e293b" }}
                              >
                                <i
                                  className={f.fromAddon ? "bx bxs-plus-circle" : "bx bxs-check-circle"}
                                  style={{ color: f.fromAddon ? MODULE : GREEN }}
                                />
                                {f.label}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Paketin serbest metin maddeleri. Yukarıdaki rozetler gerçek özellik
                        kümesinden gelir ve kısıtları belirler; buradakiler yalnız vitrin
                        yazısıdır, o yüzden ayrı başlık altında duruyor. */}
                    {features.length > 0 && (
                      <div className={featureGroups.length > 0 ? "pt-3 mt-3 border-top" : ""}>
                        <div style={{ fontSize: ".68rem", textTransform: "uppercase", letterSpacing: ".04em", color: "#94a3b8", fontWeight: 700, marginBottom: ".35rem" }}>
                          Ek Özellikler
                        </div>
                        {features.map((feature, idx) => (
                          <div key={idx} className="d-flex align-items-center gap-2 py-1">
                            <i className="bx bx-check-circle" style={{ color: GREEN, fontSize: "1rem" }} />
                            <span style={{ fontSize: ".88rem", color: "#1e293b" }}>{feature}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

      </div>
    </div>
  )
}

export default SubscriptionDetails
